import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { Link as NextLink } from '@eneco/next';
import { MessageBar as SparkyMessageBar } from '@eneco/sparky';
import type { MessageBarProps } from '@eneco/sparky/types';

import type { MessageBarContent } from './message-bar-content.types';

/**
 * `MessageBar` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/MessageBar/MessageBar.tsx`): the bar
 * inside the container's `Link`, which hands it the link's `href` (and `target` on an external link), so
 * Sparky's `MessageBar` renders its own link layer. A link in the message text then stays a separate link,
 * not one nested in another.
 */
const MessageBar: FC = () => {
  const { adapter } = useCms();
  const content = useContent<MessageBarContent>();
  const headingLevel = useSetting('headingLevel') as MessageBarProps['headingLevel'];

  const html = adapter.toRichText(content.content);
  const link = linkWithUrl(content.link);

  const bar = <SparkyMessageBar headingLevel={headingLevel} text={html ? <RichTextComponent html={html} /> : undefined} title={content.title} />;

  return link?.href ? (
    <NextLink linkValue={{ href: link.href, target: link.target }} linkType={link.linkType ?? ''}>
      {bar}
    </NextLink>
  ) : (
    bar
  );
};

export default MessageBar;
